import {type AppBskyVideoDefs, type BskyAgent} from '@atproto/api'
import {type I18n} from '@lingui/core'
import {msg} from '@lingui/core/macro'
import {nanoid} from 'nanoid/non-secure'

import {AbortError} from '#/lib/async/cancelable'
import {VIDEO_SERVICE_DID} from '#/lib/constants'
import {ServerError} from '#/lib/media/video/errors'
import {type CompressedVideo} from '#/lib/media/video/types'
import {logger} from '#/logger'
import {getServiceAuthToken, getVideoUploadLimits} from './upload.shared'
import {createVideoEndpointUrl, mimeToExt} from './util'

export async function uploadVideo({
  video,
  agent,
  did,
  setProgress,
  signal,
  i18n,
}: {
  video: CompressedVideo
  agent: BskyAgent
  did: string
  setProgress: (progress: number) => void
  signal: AbortSignal
  i18n: I18n
}) {
  if (signal.aborted) {
    throw new AbortError()
  }
  await getVideoUploadLimits(agent, i18n)

  const uri = createVideoEndpointUrl('/xrpc/app.bsky.video.uploadVideo', {
    did,
    name: `${nanoid(12)}.${mimeToExt(video.mimeType)}`,
  })

  let bytes = video.bytes
  if (!bytes) {
    if (signal.aborted) {
      throw new AbortError()
    }
    bytes = await fetch(video.uri).then(res => res.arrayBuffer())
  }

  if (signal.aborted) {
    throw new AbortError()
  }
  const token = await getServiceAuthToken({
    agent,
    aud: VIDEO_SERVICE_DID,
    lxm: 'com.atproto.repo.uploadBlob',
    exp: Date.now() / 1000 + 60 * 30, // 30 minutes
  })

  if (signal.aborted) {
    throw new AbortError()
  }
  const xhr = new XMLHttpRequest()
  const res = await new Promise<AppBskyVideoDefs.JobStatus>(
    (resolve, reject) => {
      xhr.upload.addEventListener('progress', e => {
        const progress = e.loaded / e.total
        setProgress(progress)
      })
      xhr.onloadend = () => {
        if (signal.aborted) {
          reject(new AbortError())
        } else if (xhr.readyState === 4) {
          logger.debug('Video upload XHR completed', {
            status: xhr.status,
            responseText: xhr.responseText?.slice(0, 500),
          })
          try {
            const parsed = JSON.parse(xhr.responseText)
            const uploadRes = (parsed.jobStatus ??
              parsed) as AppBskyVideoDefs.JobStatus
            resolve(uploadRes)
          } catch (parseErr) {
            logger.error('Video upload response parse failed', {
              safeMessage: parseErr,
              status: xhr.status,
              responseText: xhr.responseText?.slice(0, 500),
            })
            reject(new ServerError(i18n._(msg`Failed to upload video`)))
          }
        } else {
          logger.error('Video upload XHR ended with unexpected readyState', {
            readyState: xhr.readyState,
            status: xhr.status,
          })
          reject(new ServerError(i18n._(msg`Failed to upload video`)))
        }
      }
      xhr.onerror = () => {
        logger.error('Video upload XHR network error', {
          status: xhr.status,
          readyState: xhr.readyState,
        })
        reject(new ServerError(i18n._(msg`Failed to upload video`)))
      }
      xhr.open('POST', uri)
      xhr.setRequestHeader('Content-Type', video.mimeType)
      xhr.setRequestHeader('Authorization', `Bearer ${token}`)
      xhr.send(bytes)
    },
  )

  if (!res.jobId) {
    logger.error('Video upload response missing jobId', {
      safeMessage: res.error,
      response: JSON.stringify(res).slice(0, 500),
    })
    throw new ServerError(res.error || i18n._(msg`Failed to upload video`))
  }

  if (signal.aborted) {
    throw new AbortError()
  }
  return res
}
